/**
 * The keyboard rules of a dialog: Enter confirms its call to action, and
 * Escape closes it.
 *
 * Both frontends carry the same rules, so the decision lives here instead of in
 * the React dialog and in the Vue dialog separately. The logic touches only the
 * DOM, so `apps/gto-wizard-web` and `apps/gto-wizard-web-vue` share one copy.
 * The React dialog gets Escape from Base UI and uses only the Enter rule here.
 *
 * The dialog clicks the real call-to-action element instead of calling a
 * callback. The element already carries the answer to "is confirming allowed
 * right now" in its `disabled` state, and the browser ignores a click on a
 * disabled button. A callback would make every dialog declare that same
 * condition a second time.
 *
 * A dialog that lays a confirmation over its own body has two ways to keep
 * Enter off the buttons underneath. Mark the covered markup `inert`, which also
 * takes it out of Tab order and out of the accessibility tree — the right
 * answer when the cover is opaque. Or turn the popup's `confirmOnEnter` off,
 * which stops only Enter — the only option when the covering state does not
 * reach the markup being covered.
 *
 * A dialog whose action cannot be undone turns `confirmOnEnter` off as well.
 * Enter confirms what is reversible or cheap to redo; a delete with no undo
 * answers to a click or a deliberate Tab. Spreading Enter across every modal is
 * what makes that exception necessary rather than optional: it teaches the
 * reflex, and the user reaches the confirmation still holding the key.
 */

/** Marks the container that holds the action buttons of a dialog. */
export const DIALOG_ACTIONS_ATTRIBUTE = 'data-dialog-actions'

/** Marks one element as the call to action, ahead of the last-button rule. */
export const DIALOG_CTA_ATTRIBUTE = 'data-dialog-cta'

/** Marks a dialog that must ignore Enter. */
export const DIALOG_NO_CONFIRM_ATTRIBUTE = 'data-dialog-no-confirm'

/** Marks the root element of a dialog that has no `role="dialog"` of its own. */
export const DIALOG_ROOT_ATTRIBUTE = 'data-dialog-root'

const ACTIONS_SELECTOR = `[${DIALOG_ACTIONS_ATTRIBUTE}]`
const CTA_SELECTOR = `[${DIALOG_CTA_ATTRIBUTE}]`
const ROOT_SELECTOR = `[role="dialog"], [${DIALOG_ROOT_ATTRIBUTE}]`

/**
 * Elements that give Enter a meaning of their own. The dialog must not take it
 * from them: Enter on the Cancel button cancels, Enter in a textarea starts a
 * new line, and Enter on a menu item picks that item.
 */
const ENTER_OWNER_SELECTOR = [
  'textarea',
  'select',
  'button',
  'a[href]',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[contenteditable="plaintext-only"]',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="reset"]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'input[type="file"]',
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="textbox"]',
].join(', ')

/** Elements that can act as the call to action of a dialog. */
const CANDIDATE_SELECTOR = 'button, [role="button"], a[href]'

/**
 * Where the key press came from, relative to one dialog.
 *
 * - `inside` — the dialog owns the press.
 * - `elsewhere` — another dialog owns it, so this one must stay quiet.
 * - `page` — no dialog owns it, for example while focus rests on the document
 *   body. The legacy Vue dialogs never move focus on open, so this is their
 *   normal case.
 */
export type KeyOrigin = 'inside' | 'elsewhere' | 'page'

const isElement = (node: unknown): node is HTMLElement =>
  typeof HTMLElement !== 'undefined' && node instanceof HTMLElement

/**
 * Submit controls, spelled the way the browser reads them. A `button` inside a
 * form submits it unless the markup says otherwise, so an explicit
 * `type="submit"` is not required and must not be required here either.
 */
const SUBMIT_CONTROL_SELECTOR = [
  'button:not([type="button"]):not([type="reset"])',
  'input[type="submit"]',
  'input[type="image"]',
].join(', ')

/**
 * A form with a submit control already confirms itself on Enter. A second
 * click on the call to action would run the same action twice.
 */
const submitsItsOwnForm = (target: HTMLElement): boolean => {
  const form = target.closest('form')

  return form !== null && form.querySelector(SUBMIT_CONTROL_SELECTOR) !== null
}

/** Reports whether a click on `element` can reach its handler. */
const isActionable = (element: HTMLElement): boolean => {
  if (element.hasAttribute('disabled') || element.getAttribute('aria-disabled') === 'true') {
    return false
  }
  if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') {
    return false
  }
  // A dialog can lay a confirmation over its own body and leave the body
  // mounted, so the buttons under the cover stay in the document. `inert` is
  // how the covering view says that nothing below it answers a click.
  if (element.closest('[inert]') !== null) {
    return false
  }
  const view = element.ownerDocument.defaultView
  if (view === null) {
    return true
  }
  const style = view.getComputedStyle(element)

  // `pointer-events` covers the legacy Vue buttons, which show a disabled call
  // to action with a class instead of the `disabled` attribute. `display` and
  // `visibility` cover a dialog that renders a phone variant and a desktop
  // variant of the same button and hides one of them with a class.
  return style.pointerEvents !== 'none' && style.display !== 'none' && style.visibility !== 'hidden'
}

/**
 * Reports whether this key press is an Enter that no other control has claimed.
 *
 * It answers only "is the key itself free", never "which dialog gets it" —
 * `locateKeyOrigin` answers that.
 */
export const enterIsFreeToConfirm = (event: KeyboardEvent): boolean => {
  if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) {
    return false
  }
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return false
  }

  const target = event.target
  if (!isElement(target)) {
    // Focus can rest on the document itself, which still confirms the dialog.
    return target === null || target instanceof Document
  }

  return target.closest(ENTER_OWNER_SELECTOR) === null && !submitsItsOwnForm(target)
}

/** Reports where `event` came from, relative to `dialog`. */
export const locateKeyOrigin = (event: KeyboardEvent, dialog: HTMLElement): KeyOrigin => {
  const target = event.target
  if (!isElement(target)) {
    return 'page'
  }
  if (dialog.contains(target)) {
    return 'inside'
  }

  return target.closest(ROOT_SELECTOR) === null ? 'page' : 'elsewhere'
}

/**
 * What a dialog says about its call to action.
 *
 * - `found` — an element a click can reach right now.
 * - `blocked` — the dialog names one, and it refuses the click: a Save that
 *   waits on a request, or a form that does not validate. The caller must not
 *   reach around it for a confirm path of its own; the dialog already said no.
 * - `absent` — the dialog says nothing a click can use. The legacy Vue action
 *   rows build their buttons from plain `div` elements, so a row can exist and
 *   still answer nothing. A caller may then ask the dialog itself.
 */
export type DialogCtaLookup =
  | { kind: 'found'; element: HTMLElement }
  | { kind: 'blocked' }
  | { kind: 'absent' }

/**
 * Reports what `dialog` offers Enter.
 *
 * An element marked with `data-dialog-cta` wins. Otherwise the last button of
 * the action row wins, because every dialog of both apps lays its footer out as
 * "cancel first, confirm last". The rule never steps back to an earlier button:
 * that would press Cancel while Save waits on a request.
 */
export const locateDialogCta = (dialog: HTMLElement): DialogCtaLookup => {
  // A dialog may mark the same call to action twice, once per responsive
  // variant, and hide the variant that does not apply. Take the first one a
  // click can actually reach.
  const marked = [...dialog.querySelectorAll<HTMLElement>(CTA_SELECTOR)]
  if (marked.length > 0) {
    const reachable = marked.find(isActionable)

    return reachable === undefined ? { kind: 'blocked' } : { kind: 'found', element: reachable }
  }

  const actions = dialog.querySelector<HTMLElement>(ACTIONS_SELECTOR)
  const last =
    actions === null
      ? undefined
      : [...actions.querySelectorAll<HTMLElement>(CANDIDATE_SELECTOR)].at(-1)
  if (last === undefined) {
    return { kind: 'absent' }
  }

  return isActionable(last) ? { kind: 'found', element: last } : { kind: 'blocked' }
}

/** Returns the call to action of `dialog`, or `null` when Enter must do nothing. */
export const findDialogCta = (dialog: HTMLElement): HTMLElement | null => {
  const lookup = locateDialogCta(dialog)

  return lookup.kind === 'found' ? lookup.element : null
}

type DialogKeyOptions = {
  /**
   * Origins this dialog answers to. React renders each dialog in its own
   * portal but sends events up the component tree, so a lower dialog must hear
   * only `inside`. The Vue app resolves the top dialog itself before it calls
   * in, and its dialogs leave focus on the page, so it also answers to `page`.
   */
  origins?: readonly KeyOrigin[]
}

/** Reports whether Enter must confirm `dialog`. */
export const confirmsOnEnter = (
  event: KeyboardEvent,
  dialog: HTMLElement,
  { origins = ['inside'] }: DialogKeyOptions = {},
): boolean => {
  if (dialog.hasAttribute(DIALOG_NO_CONFIRM_ATTRIBUTE) || !enterIsFreeToConfirm(event)) {
    return false
  }

  return origins.includes(locateKeyOrigin(event, dialog))
}

/**
 * Clicks the call to action of `dialog` when `event` asks for it.
 *
 * Returns `true` when the dialog took the key press, so a caller can fall back
 * to its own confirm path when it did not.
 */
export const confirmDialogOnEnter = (
  event: KeyboardEvent,
  dialog: HTMLElement,
  options: DialogKeyOptions = {},
): boolean => {
  if (!confirmsOnEnter(event, dialog, options)) {
    return false
  }

  const cta = findDialogCta(dialog)
  if (cta === null) {
    return false
  }

  // A modal owns the keyboard while it is open, so a key it has spent must not
  // also reach the page underneath. `preventDefault` only tells a listener that
  // the key was taken, and a page shortcut that does not read `defaultPrevented`
  // still runs its own action on the same press — one press, two actions. A
  // listener that does read it loses nothing here, because it was going to do
  // nothing anyway.
  event.preventDefault()
  event.stopPropagation()
  cta.click()

  return true
}

/**
 * Reports whether this key press is an Escape that no other control has claimed.
 *
 * A control that gives Escape a meaning of its own — a time picker that reverts
 * its edit, a menu that folds — calls `preventDefault` on the key press, and
 * that answer wins. An active input method editor uses Escape to drop the text
 * it is composing, so a dialog must leave that press alone as well.
 *
 * It answers only "is the key itself free". `findPressedDialogRoot` answers
 * which dialog the press came from, because a stack owner decides that for
 * itself: the dialog on top is modal and takes the key even when focus stayed
 * in the one underneath, and even before its own markup has rendered.
 */
export const escapeIsFreeToClose = (event: KeyboardEvent): boolean => {
  if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) {
    return false
  }

  return !(event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
}

/** Returns the dialog the key press came from, or `null` when no dialog owns it. */
export const findPressedDialogRoot = (event: KeyboardEvent): HTMLElement | null => {
  const target = event.target

  return isElement(target) ? target.closest<HTMLElement>(ROOT_SELECTOR) : null
}
